import {
  type ConditionOption,
  SUGGESTED_CONDITIONS,
} from '@components/app/HomePage/SearchWidget/consts'
import { Icon } from '@components/primitives/Icon'
import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark'
import isEqual from 'lodash/isEqual'
import React from 'react'

interface SearchSuggestionInputProps<T> {
  label: string
  onClearClick: (item: T) => void
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSuggestionClick: (item: T) => void
  placeholder: string
  renderSuggestion: (item: T) => string
  suggestions: T[]
  value: string
}

export function SearchSuggestionInput<T>({
  label,
  onClearClick,
  onInputChange,
  onSuggestionClick,
  placeholder,
  renderSuggestion,
  suggestions,
  value,
}: SearchSuggestionInputProps<T>) {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && suggestions.length > 0) {
      e.preventDefault()
      const firstSuggestion = suggestions[0]
      if (firstSuggestion) {
        onSuggestionClick(firstSuggestion)
      }
    }
  }

  const isSuggestedConditions = isEqual(
    suggestions,
    SUGGESTED_CONDITIONS.map((c) => c.condition),
  )

  return (
    <div
      className={classMerge(
        'group/suggestion flex-1 cursor-pointer px-3 py-2 lg:px-4 lg:py-3',
        'transition-colors duration-200 focus-within:bg-white hover:bg-neutral100 focus-within:hover:bg-white',
      )}
    >
      <Text className='-mb-1.5 select-none' styleName='p-3xtiny'>
        {label}
      </Text>
      <div className='relative'>
        <input
          className={classMerge(
            'w-full truncate bg-transparent text-neutral900 placeholder:text-neutral400 focus:outline-none',
            'text-[length:0.8125rem] font-semibold leading-[140%] lg:text-[length:1rem] lg:font-bold lg:leading-[180%]',
            value ? 'pr-8' : '',
          )}
          onChange={onInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          value={value}
        />
        {value && (
          <button
            aria-label='Clear selected item'
            className={classMerge(
              'absolute -top-1 right-0 transition-opacity duration-200',
              'opacity-0 group-focus-within/suggestion:opacity-100',
            )}
            onClick={() => onClearClick(value as T)}
            type='button'
          >
            <Icon className='text-neutral400' icon={faXmark} size='lg' />
          </button>
        )}
      </div>
      <div
        className={classMerge(
          'z-50 w-[343px] rounded-b-lg border-x-2 border-b-2 border-neutral200 bg-white p-6 lg:w-[566px]',
          'transition-opacity duration-200',
          'absolute left-0',
          'pointer-events-none opacity-0 group-focus-within/suggestion:pointer-events-auto group-focus-within/suggestion:opacity-100',
          suggestions.length === 0 && 'hidden',
        )}
        onMouseDown={(e) => e.preventDefault()}
      >
        {isSuggestedConditions ? (
          <SuggestedConditionsList
            onSuggestionClick={onSuggestionClick as (item: string) => void}
            suggestions={SUGGESTED_CONDITIONS}
          />
        ) : (
          <ul tabIndex={-1}>
            {suggestions.map((item, index) => (
              <li
                className={classMerge(
                  'mx-2 my-0 flex flex-row items-center justify-between rounded-2xl px-4 py-2 text-base font-medium hover:cursor-pointer hover:bg-neutral200',
                  index === 0 && !isSuggestedConditions && 'mt-0',
                )}
                key={index}
                onClick={() => onSuggestionClick(item)}
                onMouseDown={(e) => e.preventDefault()}
              >
                {renderSuggestion(item)}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function SuggestedConditionsList({
  onSuggestionClick,
  suggestions,
}: {
  onSuggestionClick: (item: string) => void
  suggestions: ConditionOption[]
}) {
  return (
    <>
      <span className='px-4 py-2 text-[length:0.6875rem]'>
        Suggested Conditions
      </span>
      <ul className='max-h-96 overflow-y-scroll' tabIndex={-1}>
        {suggestions.map((item, index) => (
          <li
            className={classMerge(
              'my-0 flex flex-row items-center justify-between rounded-2xl py-0 hover:cursor-pointer hover:bg-neutral200',
            )}
            key={index}
            onClick={() => onSuggestionClick(item.condition)}
            onMouseDown={(e) => e.preventDefault()}
          >
            <div className='m-0 flex items-center gap-4 p-3'>
              <Icon
                className={classMerge(
                  'size-6 rounded-lg bg-neutral50 p-2 text-neutral600',
                  item.color,
                  item.bgColor,
                )}
                icon={item.icon}
              />
              <Text className='text-base font-medium' styleName='p-small'>
                {item.condition}
              </Text>
            </div>
          </li>
        ))}
      </ul>
    </>
  )
}
